import { createContext, useContext, useReducer, ReactNode } from 'react';
import { EarlyWarningData } from '@/types/early-warning';

interface EarlyWarningState {
  data: EarlyWarningData | null;
  isDirty: boolean;
}

type EarlyWarningAction = 
  | { type: 'UPDATE_DATA'; payload: Partial<EarlyWarningData> }
  | { type: 'CLEAR_DATA' }
  | { type: 'MARK_SAVED' };

const initialState: EarlyWarningState = {
  data: null,
  isDirty: false
};

function earlyWarningReducer(state: EarlyWarningState, action: EarlyWarningAction): EarlyWarningState {
  switch (action.type) {
    case 'UPDATE_DATA':
      return {
        data: state.data ? { ...state.data, ...action.payload } : action.payload as EarlyWarningData,
        isDirty: true
      };
    case 'CLEAR_DATA':
      return initialState;
    case 'MARK_SAVED':
      return {
        ...state,
        isDirty: false
      };
    default:
      return state;
  }
}

const EarlyWarningContext = createContext<{
  state: EarlyWarningState;
  dispatch: React.Dispatch<EarlyWarningAction>;
} | null>(null);

export function EarlyWarningProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(earlyWarningReducer, initialState);

  return (
    <EarlyWarningContext.Provider value={{ state, dispatch }}>
      {children}
    </EarlyWarningContext.Provider>
  );
}

export function useEarlyWarning() {
  const context = useContext(EarlyWarningContext);
  if (!context) {
    throw new Error('useEarlyWarning must be used within EarlyWarningProvider');
  }
  return context;
}